// API helpers for fetching CMS content from the public endpoints at runtime

const BASE_URL = 'https://api.dev.auxo.dk/public';

/**
 * Fetch all pages from the pages collection
 */
export async function fetchPages() {
  try {
    const res = await fetch(`${BASE_URL}/content/pages`);
    if (!res.ok) throw new Error('Failed to fetch pages');
    return await res.json();
  } catch (err) {
    console.error('Error fetching pages:', err);
    return [];
  }
}

/**
 * Fetch a single page by its page_slug
 */
export async function fetchPageBySlug(slug) {
  const pages = await fetchPages();
  return pages.find(p => p.page_slug === slug) || null;
}

/**
 * Fetch projects with optional category filtering (server-side)
 * Uses the v2 endpoint to support filtering and pagination structure
 */
export async function fetchProjects(categorySlug = null) {
  try {
    let url = `${BASE_URL}/v2/content/projects`;
    if (categorySlug) {
      url += `?term=category:${categorySlug}`;
    }
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch projects');
    const data = await res.json();
    // Return the items array from the v2 response structure
    return data.items || [];
  } catch (err) {
    console.error('Error fetching projects:', err);
    return [];
  }
}

/**
 * Fetch a single project by its project_slug
 */
export async function fetchProjectBySlug(slug) {
  try {
    const res = await fetch(`${BASE_URL}/content/projects`);
    if (!res.ok) throw new Error('Failed to fetch projects');
    const items = await res.json();
    return items.find(p => p.project_slug === slug) || null;
  } catch (err) {
    console.error('Error fetching project by slug:', err);
    return null;
  }
}

/**
 * Fetch all galleries
 */
export async function fetchGalleries() {
  try {
    const res = await fetch(`${BASE_URL}/content/galleries`);
    if (!res.ok) throw new Error('Failed to fetch galleries');
    return await res.json();
  } catch (err) {
    console.error('Error fetching galleries:', err);
    return [];
  }
}

/**
 * Fetch a single gallery by its gallery_slug
 */
export async function fetchGalleryBySlug(slug) {
  const galleries = await fetchGalleries();
  return galleries.find(g => g.gallery_slug === slug) || null;
}

/**
 * Fetch all terms for the category taxonomy
 */
export async function fetchCategoryTerms() {
  try {
    const res = await fetch(`${BASE_URL}/taxonomies/category/terms`);
    if (!res.ok) throw new Error('Failed to fetch category terms');
    return await res.json();
  } catch (err) {
    console.error('Error fetching category terms:', err);
    return [];
  }
}

/**
 * Fetch all menu items from the menu_items collection
 */
export async function fetchMenuItems() {
  try {
    const res = await fetch(`${BASE_URL}/content/menu_items`);
    if (!res.ok) throw new Error('Failed to fetch menu items');
    const items = await res.json();
    // Sort by order field
    return items.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (err) {
    console.error('Error fetching menu items:', err);
    return [];
  }
}

/**
 * Fetch all services from the services collection
 */
export async function fetchServices() {
  try {
    const res = await fetch(`${BASE_URL}/content/services`);
    if (!res.ok) throw new Error('Failed to fetch services');
    const items = await res.json();
    // Sort by order field
    return items.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (err) {
    console.error('Error fetching services:', err);
    return [];
  }
}

/**
 * Fetch a single service by its service_slug
 */
export async function fetchServiceBySlug(slug) {
  const services = await fetchServices();
  return services.find(s => s.service_slug === slug) || null;
}
